/**
 * InspectionNotePresetsPage.tsx — owner-only editor for inspection note presets.
 *
 * WP-07. This is the screen that makes the owner's request true without code
 * changes: for any inspection item or category they choose the QUICK VALUES and
 * the LABEL those values appear under.
 *
 *   Brakes   -> 2mm, 3mm, 4mm      labelled "Wear"
 *   Tires    -> 3/32, 12/32        labelled "Wear Amount"
 *   Exterior -> free or preset     labelled "Damage Location"
 *
 * The label is a text field, not a dropdown of known kinds — a shop can invent
 * a measurement this codebase has never heard of and it will render correctly.
 *
 * Each value may carry a unit and a severity. Severity is what lets a value
 * drive the condition indicator (2mm -> Fail); thresholds are entered here, per
 * shop, and appear nowhere in the source.
 *
 * Retiring a preset never touches inspection history: an inspection stores a
 * COPY of the value and label it was given, not a reference to this table.
 *
 * Route: /owner/settings/inspection-note-presets (owner-only)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Popconfirm,
  App,
  Spin,
  Tag,
  Empty,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  getNotePresetGroups,
  createNotePresetGroup,
  updateNotePresetGroup,
  deleteNotePresetGroup,
  createNotePresetValue,
  updateNotePresetValue,
  deleteNotePresetValue,
  reorderNotePresetValues,
  DviNotePresetGroup,
  DviNotePresetValue,
  PresetScopeType,
  PresetSeverity,
} from '../../services/api/dviNotePresetsApi';

const { Title, Paragraph, Text } = Typography;

const SCOPE_OPTIONS: Array<{ value: PresetScopeType; label: string }> = [
  { value: 'shop', label: 'All items (shop default)' },
  { value: 'category', label: 'Category' },
  { value: 'point', label: 'Specific item' },
];

const SEVERITY_OPTIONS: Array<{ value: PresetSeverity | ''; label: string }> = [
  { value: '', label: 'No condition' },
  { value: 'pass', label: 'Pass' },
  { value: 'attention', label: 'Attention' },
  { value: 'fail', label: 'Fail' },
];

const severityTag = (s: PresetSeverity | null) => {
  if (s === 'pass') return <Tag color="success">Pass</Tag>;
  if (s === 'attention') return <Tag color="warning">Attention</Tag>;
  if (s === 'fail') return <Tag color="error">Fail</Tag>;
  return <Text type="secondary">—</Text>;
};

const InspectionNotePresetsPageInner: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const rolePrefix = location.pathname.startsWith('/owner') ? '/owner' : '/admin';

  const [groups, setGroups] = useState<DviNotePresetGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // New-group form
  const [newScopeType, setNewScopeType] = useState<PresetScopeType>('category');
  const [newScopeValue, setNewScopeValue] = useState('');
  const [newLabel, setNewLabel] = useState('');

  // New-value drafts, keyed by group id so several groups can be edited at once.
  const [valueDrafts, setValueDrafts] = useState<
    Record<number, { value: string; unit: string; severity: PresetSeverity | '' }>
  >({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setGroups(await getNotePresetGroups());
    } catch {
      message.error('Failed to load inspection note presets');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddGroup = async () => {
    const label = newLabel.trim();
    if (!label) {
      message.warning('Enter the label these values show under (e.g. "Wear")');
      return;
    }
    if (newScopeType !== 'shop' && !newScopeValue.trim()) {
      message.warning('Enter the category or item name this applies to');
      return;
    }
    setSaving(true);
    try {
      await createNotePresetGroup({
        scope_type: newScopeType,
        scope_value: newScopeType === 'shop' ? '' : newScopeValue.trim(),
        label,
        sort_order: groups.length,
      });
      setNewLabel('');
      setNewScopeValue('');
      message.success('Preset group added');
      await load();
    } catch (err: any) {
      // 409 = a group already covers that item; the server's message is precise.
      message.error(err?.response?.data?.error ?? 'Failed to add preset group');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateGroup = async (
    id: number,
    patch: { label?: string; scope_value?: string },
  ) => {
    try {
      await updateNotePresetGroup(id, patch);
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.error ?? 'Failed to update preset group');
    }
  };

  const handleDeleteGroup = async (id: number) => {
    try {
      await deleteNotePresetGroup(id);
      message.success('Preset group retired');
      await load();
    } catch {
      message.error('Failed to retire preset group');
    }
  };

  const handleAddValue = async (groupId: number) => {
    const draft = valueDrafts[groupId] ?? { value: '', unit: '', severity: '' as const };
    const value = draft.value.trim();
    if (!value) {
      message.warning('Enter a value (e.g. "2mm")');
      return;
    }
    try {
      const group = groups.find((g) => g.id === groupId);
      await createNotePresetValue(groupId, {
        value,
        unit: draft.unit.trim() || null,
        severity: draft.severity || null,
        sort_order: group?.values.length ?? 0,
      });
      setValueDrafts((d) => ({ ...d, [groupId]: { value: '', unit: '', severity: '' } }));
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.error ?? 'Failed to add value');
    }
  };

  const handleUpdateValue = async (
    id: number,
    patch: { value?: string; unit?: string | null; severity?: PresetSeverity | null },
  ) => {
    try {
      await updateNotePresetValue(id, patch);
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.error ?? 'Failed to update value');
    }
  };

  const handleDeleteValue = async (id: number) => {
    try {
      await deleteNotePresetValue(id);
      await load();
    } catch {
      message.error('Failed to remove value');
    }
  };

  const moveValue = async (group: DviNotePresetGroup, index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= group.values.length) return;
    const ids = group.values.map((v) => v.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    try {
      await reorderNotePresetValues(group.id, ids);
      await load();
    } catch {
      message.error('Failed to reorder values');
    }
  };

  const valueColumns = (group: DviNotePresetGroup): ColumnsType<DviNotePresetValue> => [
    {
      title: group.label || 'Value',
      dataIndex: 'value',
      render: (_: unknown, row) => (
        <Input
          defaultValue={row.value}
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next && next !== row.value) handleUpdateValue(row.id, { value: next });
          }}
          style={{ minWidth: 120 }}
        />
      ),
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      width: 110,
      render: (_: unknown, row) => (
        <Input
          defaultValue={row.unit ?? ''}
          placeholder="optional"
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next !== (row.unit ?? '')) handleUpdateValue(row.id, { unit: next || null });
          }}
        />
      ),
    },
    {
      title: 'Sets condition',
      dataIndex: 'severity',
      width: 170,
      render: (_: unknown, row) => (
        <Select
          value={row.severity ?? ''}
          options={SEVERITY_OPTIONS}
          style={{ width: '100%' }}
          onChange={(v) => handleUpdateValue(row.id, { severity: (v || null) as PresetSeverity | null })}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_: unknown, row, index) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            aria-label="Move up"
            disabled={index === 0}
            onClick={() => moveValue(group, index, -1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            aria-label="Move down"
            disabled={index === group.values.length - 1}
            onClick={() => moveValue(group, index, 1)}
          />
          <Popconfirm
            title="Remove this value?"
            description="Inspections that already used it keep their text."
            onConfirm={() => handleDeleteValue(row.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} aria-label="Remove value" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: '0 auto' }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(`${rolePrefix}/settings`)}
        style={{ marginBottom: 12 }}
      >
        Back to Settings
      </Button>

      <Title level={3}>Inspection Note Presets</Title>
      <Paragraph type="secondary">
        Quick values technicians can tap while filling in an inspection, and the label
        they appear under. Brakes might offer <Text code>2mm / 3mm / 4mm</Text> under
        “Wear”, tires <Text code>3/32 / 12/32</Text> under “Wear Amount”, and exterior
        items a “Damage Location”. Give a value a condition to have picking it also mark
        the item Pass, Attention or Fail.
      </Paragraph>

      <Card title="Add a preset group" style={{ marginBottom: 16 }}>
        <Space wrap align="start" style={{ width: '100%' }}>
          <Select
            value={newScopeType}
            options={SCOPE_OPTIONS}
            onChange={(v) => setNewScopeType(v)}
            style={{ width: 220 }}
          />
          {newScopeType !== 'shop' && (
            <Input
              value={newScopeValue}
              onChange={(e) => setNewScopeValue(e.target.value)}
              placeholder={newScopeType === 'category' ? 'Category name (e.g. Brakes)' : 'Item name (e.g. Front brake pads)'}
              style={{ width: 260 }}
            />
          )}
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder='Label (e.g. "Wear", "Damage Location")'
            style={{ width: 240 }}
            onPressEnter={handleAddGroup}
          />
          <Button type="primary" icon={<PlusOutlined />} loading={saving} onClick={handleAddGroup}>
            Add
          </Button>
        </Space>
      </Card>

      {groups.length === 0 ? (
        <Empty description="No preset groups yet" />
      ) : (
        groups.map((group) => {
          const draft = valueDrafts[group.id] ?? { value: '', unit: '', severity: '' as const };
          return (
            <Card
              key={group.id}
              style={{ marginBottom: 16 }}
              title={
                <Space wrap>
                  <Text type="secondary">
                    {group.scope_type === 'shop'
                      ? 'All items'
                      : `${group.scope_type === 'category' ? 'Category' : 'Item'}: ${group.scope_value}`}
                  </Text>
                  <Text type="secondary">shows as</Text>
                  <Input
                    defaultValue={group.label}
                    aria-label="Measurement label"
                    onBlur={(e) => {
                      const next = e.target.value.trim();
                      if (next && next !== group.label) handleUpdateGroup(group.id, { label: next });
                    }}
                    style={{ width: 200 }}
                  />
                </Space>
              }
              extra={
                <Popconfirm
                  title="Retire this group?"
                  description="Past inspections keep the values they recorded."
                  onConfirm={() => handleDeleteGroup(group.id)}
                >
                  <Button danger size="small" icon={<DeleteOutlined />}>
                    Retire
                  </Button>
                </Popconfirm>
              }
            >
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={group.values}
                columns={valueColumns(group)}
                locale={{ emptyText: 'No values yet — technicians will see a free-text field' }}
              />

              <Space wrap style={{ marginTop: 12 }}>
                <Input
                  value={draft.value}
                  onChange={(e) =>
                    setValueDrafts((d) => ({ ...d, [group.id]: { ...draft, value: e.target.value } }))
                  }
                  placeholder='Value (e.g. "2mm")'
                  style={{ width: 180 }}
                  onPressEnter={() => handleAddValue(group.id)}
                />
                <Input
                  value={draft.unit}
                  onChange={(e) =>
                    setValueDrafts((d) => ({ ...d, [group.id]: { ...draft, unit: e.target.value } }))
                  }
                  placeholder="Unit (optional)"
                  style={{ width: 140 }}
                />
                <Select
                  value={draft.severity}
                  options={SEVERITY_OPTIONS}
                  style={{ width: 170 }}
                  onChange={(v) =>
                    setValueDrafts((d) => ({ ...d, [group.id]: { ...draft, severity: v } }))
                  }
                />
                <Button icon={<PlusOutlined />} onClick={() => handleAddValue(group.id)}>
                  Add value
                </Button>
                <Text type="secondary">{severityTag(draft.severity || null)}</Text>
              </Space>
            </Card>
          );
        })
      )}
    </div>
  );
};

const InspectionNotePresetsPage: React.FC = () => (
  <App>
    <InspectionNotePresetsPageInner />
  </App>
);

export default InspectionNotePresetsPage;
